import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Link as MuiLink,
  TextField,
  Button,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MainLayout from '@/components/MainLayout';
import PhotoUploadField from '@/components/PhotoUploadField';
import BrandingColorSettings from '@/components/BrandingColorSettings';
import LeadFormsManager from '@/components/LeadFormsManager';
import LocationSettings from '@/components/LocationSettings';
import AutomationManager from '@/components/AutomationManager';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { toast } from '@/lib/toast';

const ProfileSection = () => {
  const { user, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setName(user?.name || '');
    setAvatar(user?.avatar_url || null);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch('/users/me', { name, avatar_url: avatar });
      await refreshUser();
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <PhotoUploadField label="Avatar" value={avatar} onChange={setAvatar} />
      <TextField
        label="Display name"
        size="small"
        value={name}
        onChange={(e) => setName(e.target.value)}
        sx={{ maxWidth: 360 }}
      />
      <Box>
        <Button variant="contained" onClick={handleSave} disabled={saving} data-testid="save-profile-button">
          {saving ? 'Saving…' : 'Save profile'}
        </Button>
      </Box>
    </Stack>
  );
};

// One row per integration: status dot + name + one-line state + action.
// Replaces three permanently-colored stacked Alerts.
const IntegrationRow = ({ name, connected, detail, last, onConfigure }) => (
  <Stack
    direction="row"
    spacing={1.75}
    alignItems="center"
    // Phones: name + action on the first line, detail wraps to its own full
    // line below (a single line truncated it to a few characters).
    sx={{
      py: 1.625,
      px: 0.5,
      borderBottom: last ? 0 : 1,
      borderColor: 'divider',
      flexWrap: 'wrap',
      rowGap: 0.75,
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: (t) => (connected ? t.palette.success.main : t.palette.action.disabled),
      }}
    />
    <Typography variant="body2" fontWeight={600} sx={{ width: { xs: 'auto', sm: 130 }, flexShrink: 0 }}>
      {name}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        flex: 1,
        minWidth: 0,
        flexBasis: { xs: '100%', sm: 0 },
        order: { xs: 3, sm: 0 },
        whiteSpace: { xs: 'normal', sm: 'nowrap' },
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {detail}
    </Typography>
    {connected ? (
      <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ flexShrink: 0, ml: 'auto' }}>
        Active
      </Typography>
    ) : (
      <Button size="small" variant="outlined" color="inherit" onClick={onConfigure} sx={{ borderColor: 'divider', flexShrink: 0, ml: 'auto' }}>
        Setup guide
      </Button>
    )}
  </Stack>
);

const Section = ({ title, action, innerRef, children }) => (
  <Paper ref={innerRef} variant="outlined" sx={{ p: 3, borderRadius: 3.5 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
      {action}
    </Stack>
    {children}
  </Paper>
);

const SettingsPage = () => {
  const { isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');
  const [devOpen, setDevOpen] = useState(false);
  const [emailProvider, setEmailProvider] = useState('mock');

  useEffect(() => {
    api
      .get('/health')
      .then((res) => setEmailProvider(res.data?.email_provider || 'mock'))
      .catch(() => {});
  }, []);
  const sesActive = emailProvider === 'ses';
  const refs = {
    profile: useRef(null),
    branding: useRef(null),
    leadform: useRef(null),
    automations: useRef(null),
    hours: useRef(null),
    integrations: useRef(null),
    developer: useRef(null),
  };

  // Scroll the nearest scrollable ancestor (MainLayout's content box).
  const scrollToSection = (key) => {
    setActiveSection(key);
    const el = refs[key]?.current;
    if (!el) return;
    let scroller = el.parentElement;
    while (scroller && scroller.scrollHeight <= scroller.clientHeight + 1) {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const top =
      scroller.scrollTop + el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 16;
    scroller.scrollTo({ top, behavior: 'smooth' });
  };

  const openDevGuide = () => {
    setDevOpen(true);
    scrollToSection('developer');
  };

  const navItems = [
    { key: 'profile', label: 'My profile', show: true },
    { key: 'branding', label: 'Branding', show: isAdmin },
    { key: 'leadform', label: 'Lead forms', show: isAdmin },
    { key: 'automations', label: 'Automated messages', show: isAdmin },
    { key: 'hours', label: 'Business hours', show: isAdmin },
    { key: 'integrations', label: 'Integrations', show: true },
  ].filter((i) => i.show);

  return (
    <MainLayout title="Settings">
      <Box sx={{ maxWidth: 1040, mx: 'auto', display: 'flex', gap: 4.5, alignItems: 'flex-start' }}>
        <Stack
          spacing={0.25}
          sx={{
            width: 190,
            flexShrink: 0,
            position: 'sticky',
            top: 0,
            display: { xs: 'none', md: 'flex' },
          }}
        >
          {navItems.map((item) => {
            const active = activeSection === item.key;
            return (
              <Typography
                key={item.key}
                onClick={() => scrollToSection(item.key)}
                variant="body2"
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontWeight: active ? 600 : 500,
                  color: active ? 'primary.main' : 'text.secondary',
                  bgcolor: (t) => (active ? alpha(t.palette.primary.main, 0.1) : 'transparent'),
                  '&:hover': { color: active ? 'primary.main' : 'text.primary' },
                }}
              >
                {item.label}
              </Typography>
            );
          })}
          <Typography
            onClick={openDevGuide}
            variant="body2"
            sx={{
              px: 2,
              py: 1,
              mt: 1.5,
              borderTop: 1,
              borderColor: 'divider',
              borderRadius: 0,
              cursor: 'pointer',
              fontWeight: 500,
              color: 'text.disabled',
              '&:hover': { color: 'text.secondary' },
            }}
          >
            Developer
          </Typography>
        </Stack>

        <Stack spacing={2.25} sx={{ flex: 1, minWidth: 0 }}>
          <Section title="My profile" innerRef={refs.profile}>
            <ProfileSection />
          </Section>

          {isAdmin && (
            <Section title="Branding" innerRef={refs.branding}>
              <BrandingColorSettings />
            </Section>
          )}

          {isAdmin && (
            <Section title="Lead forms" innerRef={refs.leadform}>
              <LeadFormsManager />
            </Section>
          )}

          {isAdmin && (
            <Section title="Automated messages" innerRef={refs.automations}>
              <AutomationManager />
            </Section>
          )}

          {isAdmin && (
            <Section title="Business hours & booking limits" innerRef={refs.hours}>
              <LocationSettings />
            </Section>
          )}

          <Section
            title="Integrations"
            innerRef={refs.integrations}
            action={
              <Typography variant="caption" color="text.secondary">
                {sesActive ? '2 of 3 connected' : '1 of 3 connected'}
              </Typography>
            }
          >
            <Box>
              <IntegrationRow
                name="Stripe"
                connected
                detail="Payments · connected (test mode)"
              />
              <IntegrationRow
                name="Twilio"
                connected={false}
                detail="SMS · not connected — messages logged only"
                onConfigure={openDevGuide}
              />
              <IntegrationRow
                name="Amazon SES"
                connected={sesActive}
                detail={
                  sesActive
                    ? 'Email · sending via SES'
                    : emailProvider === 'misconfigured'
                      ? 'Email · EMAIL_PROVIDER=ses but SES_FROM_EMAIL is missing'
                      : 'Email · not connected — messages logged only'
                }
                onConfigure={openDevGuide}
                last
              />
            </Box>
          </Section>

          <Paper
            ref={refs.developer}
            variant="outlined"
            sx={{ borderRadius: 3.5, borderStyle: devOpen ? 'solid' : 'dashed' }}
          >
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              onClick={() => setDevOpen((o) => !o)}
              sx={{ px: 3, py: 2, cursor: 'pointer' }}
              data-testid="developer-section-toggle"
            >
              <CodeIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.disabled" sx={{ flex: 1 }}>
                Developer — environment variables &amp; messaging setup guide
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: 'text.disabled',
                  transform: devOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 150ms ease',
                }}
              />
            </Stack>
            <Collapse in={devOpen}>
              <Box sx={{ px: 3, pb: 3 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Backend environment
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2.5, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: 12 }}
                >
                  {`DATABASE_URL=postgresql+asyncpg://...
JWT_SECRET=...
STRIPE_API_KEY=...
# Real email via Amazon SES:
EMAIL_PROVIDER=ses
SES_FROM_EMAIL=no-reply@yourdomain.com
SES_REGION=us-east-1  # + AWS credentials (env or instance role)
# Twilio (SMS) — not wired yet`}
                </Paper>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Enable real email (Amazon SES)
                </Typography>
                <Stack component="ol" spacing={1.25} sx={{ pl: 2, m: 0 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Verify your sending domain or address in{' '}
                    <MuiLink href="https://console.aws.amazon.com/ses/" target="_blank" rel="noopener noreferrer" color="primary">
                      Amazon SES
                    </MuiLink>{' '}
                    (and request production access to leave the sandbox)
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Set <code>EMAIL_PROVIDER=ses</code>, <code>SES_FROM_EMAIL</code>,{' '}
                    <code>SES_REGION</code> and AWS credentials in the backend <code>.env</code>
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Restart the backend server
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Test from the Messages page — the message status shows{' '}
                    <code>sent</code> instead of <code>mock_sent</code>
                  </Typography>
                </Stack>
              </Box>
            </Collapse>
          </Paper>
        </Stack>
      </Box>
    </MainLayout>
  );
};

export default SettingsPage;
